import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Sparkle, PencilSimpleLine, ArrowsClockwise, Eye, CaretDown, CaretRight, Users, Warning, Envelope, User, Layout, ArrowSquareOut } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { toast } from 'sonner'
import { emailBodyToHtml } from '@/lib/utils'
import { workflowsApi, contactsApi, mailTemplatesApi, getErrorMessage } from '@/lib/api'
import TemplateInput from '@/components/ui/TemplateInput'
import MaterialSelector from './MaterialSelector'
import ContactFieldSelector from './ContactFieldSelector'
import SendSchedulingSection from './SendSchedulingSection'
import type { WorkflowNodeData, WorkflowNode, WorkflowEdge, EmailGenerationMode, EmailTone, EmailPurpose, ContactContextField, ReplyScenario } from '@/types/workflow'
import type { TemplateListItem, TemplateCategory } from '@/types/template'

interface SendEmailConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
  nodes?: WorkflowNode[]
  edges?: WorkflowEdge[]
  nodeId?: string
  workflowId?: number
  upstreamEnrichmentFields?: ContactContextField[]
  upstreamCollateralMaterialIds?: number[]
  /** Notifies the parent (NodeConfigPanel) each time a fresh AI preview is
   *  generated (or cleared, via `null`), so it can hand the exact same
   *  subject/body to TestNodePanel's "Run Test" — otherwise clicking
   *  "Test This Node" after "Generate Preview" ran the LLM a second,
   *  independent time and could send different content than the preview
   *  the user just reviewed (AB#21906). */
  onPreviewGenerated?: (preview: { subject: string; body: string } | null) => void
}

interface DiscussionEntry {
  role: string
  content: string
}

interface PreviewData {
  subject: string
  body: string
  blocks?: { name: string; content: string }[]
  contact_name?: string
  discussion?: DiscussionEntry[]
  is_goodbye?: boolean
  email_step?: number
  total_emails?: number
}

/** Walk backward from nodeId through edges to compute sequence position. */
function getSequencePosition(nodes: WorkflowNode[], edges: WorkflowEdge[], nodeId: string) {
  const nodeMap = new Map(nodes.map(n => [n.id, n]))
  // reverse adjacency: target -> [{source, sourceHandle}]
  type EdgeInfo = { source: string; sourceHandle?: string }
  const reverseAdj: Record<string, EdgeInfo[]> = {}
  for (const e of edges) {
    if (!reverseAdj[e.target]) reverseAdj[e.target] = []
    reverseAdj[e.target].push({ source: e.source, sourceHandle: e.sourceHandle })
  }

  const precedingEmails: string[] = []
  let branchContext: string | null = null
  const visited = new Set<string>()
  let current: string | null = nodeId

  while (current && !visited.has(current)) {
    visited.add(current)
    const parents: EdgeInfo[] = reverseAdj[current] || []
    if (parents.length === 0) break
    const { source, sourceHandle } = parents[0]
    const parentNode = nodeMap.get(source)
    if (parentNode?.type === 'condition' && sourceHandle) {
      branchContext = sourceHandle
    }
    if (parentNode?.type === 'sendEmail') {
      precedingEmails.push(source)
    }
    current = source
  }
  precedingEmails.reverse()

  // Count all sendEmail nodes in the workflow
  const totalEmails = nodes.filter(n => n.type === 'sendEmail').length

  return {
    emailStep: precedingEmails.length + 1,
    totalEmails,
    precedingEmailNodeIds: precedingEmails,
    branchContext,
  }
}

const replyScenarios: { value: ReplyScenario; label: string; description: string }[] = [
  { value: 'no_reply', label: 'No Reply', description: 'Recipient did not respond' },
  { value: 'positive', label: 'Positive Reply', description: 'Recipient showed interest' },
  { value: 'not_interested', label: 'Not Interested', description: 'Recipient declined' },
]

const tones: { value: EmailTone; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'casual', label: 'Casual' },
  { value: 'persuasive', label: 'Persuasive' },
  { value: 'urgent', label: 'Urgent' },
]

const purposes: { value: EmailPurpose; label: string }[] = [
  { value: 'introduction', label: 'Introduction' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'pitch', label: 'Sales Pitch' },
  { value: 'nurture', label: 'Nurture' },
  { value: 're_engage', label: 'Re-engage' },
  { value: 'thank_you', label: 'Thank You' },
]

const roleConfig: Record<string, { label: string; color: string; bgColor: string; borderColor: string }> = {
  Analyst: {
    label: 'Research Analyst',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-500/15',
    borderColor: 'border-blue-500/30',
  },
  Copywriter: {
    label: 'Copywriter',
    color: 'text-[#5e6ad2] dark:text-[#a5b4fc]',
    bgColor: 'bg-[#5e6ad2]/15',
    borderColor: 'border-[#5e6ad2]/30',
  },
  Critic: {
    label: 'Quality Critic',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/15',
    borderColor: 'border-amber-500/30',
  },
  Editor: {
    label: 'Final Editor',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-500/15',
    borderColor: 'border-green-500/30',
  },
}

import { cn } from '@/lib/utils'
import {
  inputClass,
  inputStyle,
  sectionClass,
  textareaClass,
  infoBannerClass,
  warnBannerClass,
  dangerBannerClass,
  sectionEyebrowClass,
  sectionEyebrowStyle,
} from './_kit'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'

const REPLY_SCENARIO_OPTIONS = replyScenarios.map(s => ({ value: s.value, label: `${s.label} — ${s.description}` }))
const TONE_OPTIONS = tones.map(t => ({ value: t.value, label: t.label }))
const PURPOSE_OPTIONS = purposes.map(p => ({ value: p.value, label: p.label }))

const DiscussionItem: React.FC<{ entry: DiscussionEntry }> = ({ entry }) => {
  const [isOpen, setIsOpen] = useState(false)
  const config = roleConfig[entry.role] || roleConfig.Analyst

  // Try to parse JSON content for cleaner display per role
  let displayContent = entry.content
  try {
    const parsed = JSON.parse(entry.content)
    if (entry.role === 'Analyst' && parsed.opening_hook) {
      const lines = [`Hook: ${parsed.opening_hook}`]
      if (parsed.primary_pain_point) lines.push(`Pain Point: ${parsed.primary_pain_point}`)
      if (parsed.value_match) lines.push(`Value Match: ${parsed.value_match}`)
      if (parsed.tone_and_style) lines.push(`Tone: ${parsed.tone_and_style}`)
      if (parsed.differentiator) lines.push(`Differentiator: ${parsed.differentiator}`)
      if (parsed.cta_type) lines.push(`CTA: ${parsed.cta_type}`)
      if (parsed.key_facts?.length) lines.push(`\nKey Facts:\n${parsed.key_facts.map((f: string) => `  • ${f}`).join('\n')}`)
      displayContent = lines.join('\n')
    } else if (entry.role === 'Critic' && parsed.scores) {
      const s = parsed.scores
      const lines = [
        `Personalization: ${s.personalization}/10  |  Value: ${s.value_clarity}/10  |  Hook: ${s.hook_strength}/10`,
        `Authenticity: ${s.authenticity}/10  |  CTA: ${s.cta_effectiveness}/10  |  Average: ${parsed.average_score}/10`,
        parsed.approved ? '\n✓ Approved — no edits needed' : '\n✗ Needs improvement',
      ]
      if (parsed.strengths?.length) lines.push(`\nStrengths:\n${parsed.strengths.map((s: string) => `  ✓ ${s}`).join('\n')}`)
      if (parsed.improvements?.length) lines.push(`\nImprovements:\n${parsed.improvements.map((i: string) => `  → ${i}`).join('\n')}`)
      displayContent = lines.join('\n')
    } else if (parsed.approach) {
      displayContent = parsed.approach
    } else if (parsed.changes_made) {
      displayContent = parsed.changes_made
    } else if (parsed.reasoning) {
      displayContent = parsed.reasoning
    } else if (parsed.improvements && typeof parsed.improvements === 'string') {
      displayContent = parsed.improvements
    } else if (parsed.strategic_notes) {
      displayContent = parsed.strategic_notes
    }
  } catch {
    // If content contains JSON embedded in text, try to extract notes
    const notesMatch = entry.content.match(/"(?:approach|changes_made|opening_hook|reasoning|improvements|strategic_notes)"\s*:\s*"((?:[^"\\]|\\.)*)"/s)
    if (notesMatch) {
      displayContent = notesMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"')
    }
  }

  return (
    <div className={`border rounded-xl overflow-hidden ${config.borderColor}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${config.bgColor}`}
      >
        {isOpen ? <CaretDown className="w-3 h-3 flex-shrink-0" /> : <CaretRight className="w-3 h-3 flex-shrink-0" />}
        <span className={`text-xs font-emphasis ${config.color}`}>{entry.role}</span>
        <span className="text-[10px] text-muted-foreground">{config.label}</span>
      </button>
      {isOpen && (
        <div className="px-3 py-2 text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed border-t border-border/30">
          {displayContent}
        </div>
      )}
    </div>
  )
}

const categoryLabels: Record<TemplateCategory, string> = {
  cold_outreach: 'Cold Outreach',
  follow_up: 'Follow Up',
  nurture: 'Nurture',
  re_engage: 'Re-engage',
  introduction: 'Introduction',
  general: 'General',
}

const SendEmailConfig: React.FC<SendEmailConfigProps> = ({ data, updateField, nodes, edges, nodeId, workflowId, upstreamEnrichmentFields = [], upstreamCollateralMaterialIds = [], onPreviewGenerated }) => {
  const [activeTab, setActiveTab] = useState<'content' | 'preview'>('content')
  const [isGenerating, setIsGenerating] = useState(false)
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [previewMode, setPreviewMode] = useState<'blocks' | 'full'>('blocks')
  const [replyScenario, setReplyScenario] = useState<ReplyScenario>('no_reply')
  const [contacts, setContacts] = useState<{ id: number; full_name: string; email: string }[]>([])
  const [selectedContactId, setSelectedContactId] = useState<number | undefined>(undefined)
  const [templates, setTemplates] = useState<TemplateListItem[]>([])
  const [templateBlocks, setTemplateBlocks] = useState<{ name: string; instruction: string }[]>([])

  // Auto-select enrichment fields from upstream EnrichContacts node.
  // The useRef guard ensures this runs at most once per component mount —
  // even if upstreamEnrichmentFields changes identity on re-render.
  // The dependency array intentionally omits `data` and `updateField` to
  // prevent the effect re-running after it writes to node data.
  // Do NOT add them to deps — that would break the one-shot guarantee.
  const enrichmentApplied = useRef(false)
  useEffect(() => {
    if (enrichmentApplied.current) return
    if (!upstreamEnrichmentFields.length) return
    enrichmentApplied.current = true
    const current: ContactContextField[] = data.ai_config?.contact_fields || []
    const toAdd = upstreamEnrichmentFields.filter(f => !current.includes(f))
    if (toAdd.length > 0) {
      updateField('ai_config', {
        ...data.ai_config,
        contact_fields: [...current, ...toAdd],
      })
    }
  }, [upstreamEnrichmentFields])

  // Diff-reconcile materials against upstream Collaterals (generateDeck) nodes.
  // Runs when the panel opens (and if the upstream set changes). We track the
  // collateral set as of the last sync in `ai_config.collateral_material_ids`
  // (provenance) and apply only the delta:
  //   - removed from collateral  → removed here (deselect propagates)
  //   - added to collateral      → added here (if not already present)
  // Materials the user picked/removed manually in this node are left alone,
  // because we only act on the delta and never force-presence. The dependency
  // array intentionally omits `data`/`updateField`; within a single mount the
  // upstream set is stable, so this reconciles once per panel open.
  useEffect(() => {
    const prev: number[] | null = data.ai_config?.collateral_material_ids ?? null
    const upstream = upstreamCollateralMaterialIds
    // Nothing was ever synced and there's no collateral upstream → leave node as-is.
    if (prev === null && upstream.length === 0) return

    const current: number[] = data.ai_config?.material_ids || []
    const prevSet = prev ?? []
    const removed = prevSet.filter(id => !upstream.includes(id))
    const added = upstream.filter(id => !prevSet.includes(id))

    const next = current.filter(id => !removed.includes(id))
    for (const id of added) {
      if (!next.includes(id)) next.push(id)
    }

    const materialsChanged =
      next.length !== current.length || next.some((id, i) => id !== current[i])
    const provenanceChanged =
      prev === null || prev.length !== upstream.length || prev.some((id, i) => id !== upstream[i])

    if (materialsChanged || provenanceChanged) {
      updateField('ai_config', {
        ...data.ai_config,
        material_ids: next,
        collateral_material_ids: upstream,
      })
    }
  }, [upstreamCollateralMaterialIds])

  useEffect(() => {
    contactsApi.getAll().then(res => {
      setContacts(res.data.map((c: any) => ({ id: c.id, full_name: c.full_name || '', email: c.email })))
    }).catch(() => {})
    // Fetch templates for selector
    mailTemplatesApi.getAll().then(res => {
      const list: TemplateListItem[] = res.data
      setTemplates(list)
      // Auto-select default template if none selected
      if (!data.template_id) {
        const defaultTpl = list.find(t => t.is_default) || list[0]
        if (defaultTpl) updateField('template_id', defaultTpl.id)
      }
    }).catch(() => {})
  }, [])

  // Fetch block instructions for the selected template (for preview tooltips)
  useEffect(() => {
    if (data.template_id) {
      // silent: the tooltips are a nicety. A node pointing at a template that
      // no longer resolves used to surface as a red "Template not found" the
      // moment the panel opened -- the caller's .catch() never saw it, because
      // the axios interceptor toasts every error detail before the promise
      // rejects. No tooltips is the right degradation here.
      mailTemplatesApi.getById(data.template_id, { silent: true }).then(res => {
        setTemplateBlocks(res.data.blocks?.map((b: any) => ({ name: b.name, instruction: b.instruction })) || [])
      }).catch(() => setTemplateBlocks([]))
    }
  }, [data.template_id])

  // Compute sequence position
  const sequenceInfo = useMemo(() => {
    if (!nodes || !edges || !nodeId) return { emailStep: 1, totalEmails: 1, precedingEmailNodeIds: [], branchContext: null }
    return getSequencePosition(nodes, edges, nodeId)
  }, [nodes, edges, nodeId])

  const isFollowUp = sequenceInfo.emailStep > 1

  const mode: EmailGenerationMode = data.ai_config?.generation_mode || 'manual'
  const aiConfig = data.ai_config || {
    generation_mode: 'manual' as EmailGenerationMode,
    material_ids: [] as number[],
    contact_fields: ['job_title', 'company_name', 'company_industry'] as ContactContextField[],
    tone: 'professional' as EmailTone,
    purpose: 'introduction' as EmailPurpose,
    custom_instructions: '',
  }

  const setMode = (newMode: EmailGenerationMode) => {
    updateField('ai_config', { ...aiConfig, generation_mode: newMode })
  }

  const updateAI = (key: string, value: any) => {
    updateField('ai_config', { ...aiConfig, [key]: value })
  }

  // Collect previous email content from preceding nodes
  const getPreviousEmails = () => {
    if (!nodes) return []
    const nodeMap = new Map(nodes.map(n => [n.id, n]))
    return sequenceInfo.precedingEmailNodeIds.map((id, idx) => {
      const node = nodeMap.get(id)
      return {
        subject: node?.data?.subject || '(AI-generated subject)',
        body: node?.data?.body || '(AI-generated body — will use actual content at send time)',
        step: idx + 1,
      }
    })
  }

  const handleGeneratePreview = async () => {
    setIsGenerating(true)
    try {
      const res = await workflowsApi.previewEmail({
        material_ids: aiConfig.material_ids,
        contact_fields: aiConfig.contact_fields as string[],
        tone: aiConfig.tone,
        purpose: aiConfig.purpose,
        custom_instructions: aiConfig.custom_instructions,
        contact_id: selectedContactId,
        email_step: sequenceInfo.emailStep,
        total_emails: sequenceInfo.totalEmails,
        previous_emails: isFollowUp ? getPreviousEmails() : undefined,
        reply_scenario: isFollowUp ? replyScenario : undefined,
        workflow_id: workflowId,
        node_id: nodeId,
        template_id: data.template_id,
      })
      setPreview(res.data)
      setActiveTab('preview')
      onPreviewGenerated?.({ subject: res.data.subject, body: res.data.body })
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate preview. Check your LLM settings.'))
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className={sectionClass}>
      {/* Label */}
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., Welcome Email"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      {/* Sequence Position Badge */}
      {isFollowUp && (
        <div className="space-y-3">
          <div className={cn(infoBannerClass, 'items-center')}>
            <Envelope className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">
              Email {sequenceInfo.emailStep} of {sequenceInfo.totalEmails}
            </span>
            {sequenceInfo.branchContext && (
              <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-medium ${
                sequenceInfo.branchContext === 'yes'
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                  : 'bg-red-500/15 text-red-700 dark:text-red-300'
              }`}>
                {sequenceInfo.branchContext === 'yes' ? 'YES branch' : 'NO branch'}
              </span>
            )}
          </div>

          {/* Reply Scenario Selector */}
          <Field
            label="Reply Scenario (for preview)"
            hint="Simulate how the recipient responded to preview context-aware follow-ups"
          >
            {(f) => (
              <Select
                {...f}
                value={replyScenario}
                onChange={(value) => setReplyScenario(value as ReplyScenario)}
                options={REPLY_SCENARIO_OPTIONS}
                className="w-full"
              />
            )}
          </Field>

          {replyScenario === 'not_interested' && (
            <div className={cn(warnBannerClass, 'items-center')}>
              <Warning className="w-3.5 h-3.5" />
              <span className="text-xs">
                AI will generate a goodbye email. Contact will be dropped from workflow.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Mode Toggle */}
      <Field label="Generation Mode" as="group">
        <div className="flex rounded-lg border border-border/60 overflow-hidden">
          <button
            type="button"
            onClick={() => setMode('manual')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-all duration-150 ${
              mode === 'manual'
                ? 'bg-[#5e6ad2] text-white'
                : 'bg-card text-muted-foreground hover:bg-muted'
            }`}
          >
            <PencilSimpleLine className="w-3.5 h-3.5" />
            Manual
          </button>
          <button
            type="button"
            onClick={() => setMode('ai')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium transition-all duration-150 ${
              mode === 'ai'
                ? 'bg-[#5e6ad2] text-white'
                : 'bg-card text-muted-foreground hover:bg-muted'
            }`}
          >
            <Sparkle className="w-3.5 h-3.5" />
            AI Generated
          </button>
        </div>
      </Field>

      {mode === 'manual' ? (
        /* Manual Mode */
        <>
          <Field label="Subject Line">
            {(f) => (
              <TemplateInput
                {...f}
                value={data.subject || ''}
                onChange={(v) => updateField('subject', v)}
                placeholder="Enter email subject... (type {{ for variables)"
                multiline={false}
                aiAssist
                aiFieldType="subject"
              />
            )}
          </Field>
          <Field label="Email Body">
            {(f) => (
              <TemplateInput
                {...f}
                value={data.body || ''}
                onChange={(v) => updateField('body', v)}
                placeholder="Write your email content... (type {{ for variables)"
                rows={6}
                aiAssist
                aiFieldType="body"
              />
            )}
          </Field>
        </>
      ) : (
        /* AI Mode */
        <>
          {/* Tab bar */}
          <div className="flex border-b border-border/50">
            <button
              type="button"
              onClick={() => setActiveTab('content')}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-all duration-150 ${
                activeTab === 'content'
                  ? 'border-[#5e6ad2] text-[#5e6ad2] dark:text-[#a5b4fc]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              AI Settings
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('preview')}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-all duration-150 flex items-center gap-1 ${
                activeTab === 'preview'
                  ? 'border-[#5e6ad2] text-[#5e6ad2] dark:text-[#a5b4fc]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Eye className="w-3 h-3" />
              Preview
              {preview && <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />}
            </button>
          </div>

          {activeTab === 'content' ? (
            <div className={sectionClass}>
              {/* Template Selector */}
              {templates.length > 0 && (
                <Field
                  label={
                    <span className="flex items-center gap-1.5">
                      <Layout className="w-3.5 h-3.5" />
                      Email Structure Template
                    </span>
                  }
                  hint="Defines the block structure the AI follows when writing"
                >
                  {(f) => (
                    <div className="flex gap-2">
                      <Select
                        {...f}
                        value={data.template_id != null ? String(data.template_id) : ''}
                        onChange={(value) => updateField('template_id', Number(value))}
                        groups={Object.entries(
                          templates.reduce<Record<string, TemplateListItem[]>>((acc, t) => {
                            const cat = categoryLabels[t.category] || t.category
                            if (!acc[cat]) acc[cat] = []
                            acc[cat].push(t)
                            return acc
                          }, {})
                        ).map(([category, items]) => ({
                          label: category,
                          options: items.map(t => ({
                            value: String(t.id),
                            label: `${t.name}${t.is_default ? ' (Default)' : ''}${t.is_system ? ' — System' : ''}`,
                          })),
                        }))}
                        className="flex-1"
                      />
                      <Tooltip label="Edit template">
                        <a
                          href={data.template_id ? `/templates/${data.template_id}/edit` : '/templates'}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Edit template"
                          className="flex items-center justify-center px-2 text-muted-foreground hover:text-foreground transition-all duration-150"
                        >
                          <ArrowSquareOut className="w-3.5 h-3.5" />
                        </a>
                      </Tooltip>
                    </div>
                  )}
                </Field>
              )}

              {/* Material Selector */}
              <MaterialSelector
                selectedIds={aiConfig.material_ids || []}
                onChange={(ids) => updateAI('material_ids', ids)}
              />

              {/* Contact Field Selector */}
              <ContactFieldSelector
                selectedFields={(aiConfig.contact_fields || []) as ContactContextField[]}
                onChange={(fields) => updateAI('contact_fields', fields)}
                upstreamEnrichmentFields={upstreamEnrichmentFields}
              />

              {/* Tone */}
              <Field label="Tone">
                {(f) => (
                  <Select
                    {...f}
                    value={aiConfig.tone}
                    onChange={(value) => updateAI('tone', value)}
                    options={TONE_OPTIONS}
                    className="w-full"
                  />
                )}
              </Field>

              {/* Purpose */}
              <Field label="Purpose">
                {(f) => (
                  <Select
                    {...f}
                    value={aiConfig.purpose}
                    onChange={(value) => updateAI('purpose', value)}
                    options={PURPOSE_OPTIONS}
                    className="w-full"
                  />
                )}
              </Field>

              {/* Custom Instructions */}
              <Field label="Custom Instructions">
                {(f) => (
                  <textarea
                    {...f}
                    value={aiConfig.custom_instructions}
                    onChange={(e) => updateAI('custom_instructions', e.target.value)}
                    placeholder="Additional context or instructions for the AI..."
                    rows={3}
                    className={cn(textareaClass, 'h-[96px] overflow-y-auto')}
                    style={inputStyle}
                  />
                )}
              </Field>

              {/* Preview Contact Selector */}
              <Field
                label={
                  <span className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    Preview Contact
                  </span>
                }
                hint="Choose a contact to personalize the preview email for"
              >
                {(f) => (
                  <Select
                    {...f}
                    value={selectedContactId != null ? String(selectedContactId) : ''}
                    onChange={(value) => setSelectedContactId(value ? Number(value) : undefined)}
                    options={[
                      { value: '', label: 'Auto (first contact)' },
                      ...contacts.map(c => ({
                        value: String(c.id),
                        label: c.full_name ? `${c.full_name} — ${c.email}` : c.email,
                      })),
                    ]}
                    className="w-full"
                  />
                )}
              </Field>

              {/* Generate Preview */}
              <Button
                variant="default"
                size="sm"
                className="w-full bg-[#5e6ad2] hover:bg-[#7170ff]"
                onClick={handleGeneratePreview}
                loading={isGenerating}
                icon={<Sparkle className="w-3.5 h-3.5" />}
              >
                {isGenerating ? 'Analyzing → Writing → Reviewing...' : 'Generate Preview'}
              </Button>

              {isGenerating && (
                <div className={cn(infoBannerClass, 'items-center')}>
                  <Users className="w-3.5 h-3.5 animate-pulse" />
                  <p className="text-xs">
                    Analyst → Copywriter → Critic → Editor crafting your email...
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Preview Tab */
            <div className="space-y-3">
              {preview ? (
                <>
                  {preview.contact_name && (
                    <p className="text-xs text-muted-foreground">Preview for: <span className="font-medium text-foreground">{preview.contact_name}</span></p>
                  )}

                  {preview.is_goodbye && (
                    <div className={cn(dangerBannerClass, 'items-center')}>
                      <Warning className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">
                        Goodbye email — contact will be removed from further processing
                      </span>
                    </div>
                  )}

                  {preview.email_step && preview.email_step > 1 && (
                    <p className="text-[10px] text-muted-foreground">
                      Context-aware follow-up (Email {preview.email_step} of {preview.total_emails})
                    </p>
                  )}

                  {/* Block/Full toggle */}
                  {preview.blocks && preview.blocks.length > 0 && (
                    <div className="flex rounded-lg border border-border/50 overflow-hidden w-fit">
                      <button
                        type="button"
                        onClick={() => setPreviewMode('blocks')}
                        className={`px-2.5 py-1 text-[10px] font-medium transition-all duration-150 ${
                          previewMode === 'blocks'
                            ? 'bg-[#5e6ad2] text-white'
                            : 'bg-card text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Block View
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewMode('full')}
                        className={`px-2.5 py-1 text-[10px] font-medium transition-all duration-150 ${
                          previewMode === 'full'
                            ? 'bg-[#5e6ad2] text-white'
                            : 'bg-card text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Full Email
                      </button>
                    </div>
                  )}

                  {/* Email Preview */}
                  <div className="bg-muted rounded-xl p-3 space-y-2">
                    <div>
                      <p className={sectionEyebrowClass} style={sectionEyebrowStyle}>Subject</p>
                      <p className="text-sm font-medium text-foreground">{preview.subject}</p>
                    </div>
                    <div className="border-t border-border/50 pt-2">
                      {previewMode === 'blocks' && preview.blocks && preview.blocks.length > 0 ? (
                        <div className="space-y-2">
                          {preview.blocks.map((block, idx) => {
                            const instruction = templateBlocks.find(b => b.name === block.name)?.instruction
                            return (
                              <div key={idx} className="border border-border/50 rounded-lg overflow-hidden">
                                <div className="flex items-center justify-between px-2.5 py-1.5 bg-muted">
                                  <span className={sectionEyebrowClass} style={sectionEyebrowStyle}>{block.name}</span>
                                  <span className="text-[9px] text-muted-foreground font-mono">{block.content.split(/\s+/).length}w</span>
                                </div>
                                <div className="px-2.5 py-2 text-sm text-foreground leading-relaxed">
                                  {block.content}
                                </div>
                                {instruction && (
                                  <div className="px-2.5 py-1.5 border-t border-border/30 bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground italic">Instruction: {instruction}</p>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <>
                          <p className={sectionEyebrowClass} style={sectionEyebrowStyle}>Body</p>
                          <div className="text-sm text-foreground leading-relaxed [&>p]:mb-2 [&>p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: emailBodyToHtml(preview.body) }} />
                        </>
                      )}
                    </div>
                  </div>

                  {/* C-Suite Discussion */}
                  {preview.discussion && preview.discussion.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5">
                        <Users className="w-3 h-3 text-muted-foreground" />
                        <p className={sectionEyebrowClass} style={sectionEyebrowStyle}>Agent Pipeline</p>
                      </div>
                      <div className="space-y-1.5">
                        {preview.discussion.map((entry, idx) => (
                          <DiscussionItem key={idx} entry={entry} />
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleGeneratePreview}
                    loading={isGenerating}
                    icon={<ArrowsClockwise className="w-3.5 h-3.5" />}
                  >
                    Regenerate
                  </Button>
                </>
              ) : (
                <div className="text-center py-6">
                  <Sparkle className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No preview yet</p>
                  <p className="text-xs text-muted-foreground/70 mt-1">Configure AI settings and click Generate Preview</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Recipient-timezone send window (applies to both manual and AI modes) */}
      <SendSchedulingSection data={data} updateField={updateField} />
    </div>
  )
}

export default SendEmailConfig
